"use client";
import dynamic from "next/dynamic";

import type {
  AuthDialogDefaultValues,
  AuthDialogIntent,
} from "@/components/auth/dialogs/auth-dialog";
import { useBroadcastChannel } from "@/hooks/use-broadcast-channel";
import {
  type DisplayAuthDialogParams,
  userContext,
} from "@/hooks/use-current-user";
import { api } from "@/lib/api-client";
import { useApiWorkspaceActor } from "@/hooks/use-marketplace-workspace";
import { setMarketplaceWorkspace } from "@/lib/marketplace-workspace";
import { clearCache } from "@/lib/cache";
import type { User } from "@/lib/types";
import { config } from "@config";
import { type PropsWithChildren, useEffect, useState } from "react";

const RequestedAuthDialog = dynamic(() =>
  import("./requested-auth-dialog").then((m) => m.RequestedAuthDialog),
);

type AuthEvent = {
  type: "loaded" | "logout";
  user: User | null;
};

export function CurrentUserProvider({
  children,
  initialUser,
}: PropsWithChildren<{ initialUser: User | null }>) {
  const [loaded, setLoaded] = useState(!!initialUser);
  const [user, setUser] = useState<User | null>(initialUser);
  const setApiActor = useApiWorkspaceActor();
  useEffect(() => {
    if (loaded) setApiActor(user?.id ?? null);
  }, [loaded, user?.id, setApiActor]);

  const [authDialogIntent, setAuthDialogIntent] =
    useState<AuthDialogIntent>("unknown");
  const [customTitle, setCustomTitle] = useState<string | undefined>(undefined);
  const [authDialogDefaultValues, setAuthDialogDefaultValues] = useState<
    AuthDialogDefaultValues | undefined
  >();
  const [authDialogCallback, setAuthDialogCallback] =
    useState<(user?: User) => void>();
  const [skipSegmentation, setSkipSegmentation] = useState(false);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);

  const [authDialogRequested, setAuthDialogRequested] = useState(false);

  const userQuery = api.auth.user.useQuery(undefined, {
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled: !initialUser,
  });

  const logoutMutation = api.auth.logout.useMutation();

  const { postMessage } = useBroadcastChannel<AuthEvent>(
    "venturu_auth",
    (data) => {
      if (JSON.stringify(data.user) !== JSON.stringify(user)) {
        if (data.type === "logout") {
          window.location.href = config.auth.redirectAfterLogout;
        } else if (data.type === "loaded") {
          setUser(user);
        }
      }
    },
  );

  async function reloadUser() {
    await userQuery.refetch();
  }

  async function logout() {
    await logoutMutation.mutateAsync();
    setMarketplaceWorkspace(user?.id ?? "", null);
    await clearCache();

    postMessage({
      type: "logout",
      user: null,
    } satisfies AuthEvent);

    window.location.href = config.auth.redirectAfterLogout;
  }

  function displayAuthDialog({
    intent,
    defaultValues,
    title,
    onLoggedIn,
    skipSegmentation: skipSegmentationParam,
  }: DisplayAuthDialogParams = {}) {
    // Each action owns its callback and signup intent; never reuse a previous
    // inquiry callback or a previous action's skip-onboarding setting.
    setAuthDialogIntent(intent ?? "unknown");
    setAuthDialogDefaultValues(defaultValues);
    setAuthDialogCallback(onLoggedIn ? () => onLoggedIn : undefined);
    setCustomTitle(title);
    setSkipSegmentation(skipSegmentationParam ?? false);

    setAuthDialogRequested(true);
  }

  useEffect(() => {
    if (userQuery.data) {
      setUser(userQuery.data);
    }
  }, [userQuery.data]);

  useEffect(() => {
    if (userQuery.isSuccess) {
      setLoaded(true);
    }
  }, [userQuery.isSuccess]);

  useEffect(() => {
    if (user && loaded) {
      postMessage({
        type: "loaded",
        user,
      });
    }
  }, [user, loaded]);

  useEffect(() => {
    if (!authDialogOpen) {
      // reset intent and callback
      setAuthDialogIntent("unknown");
      setAuthDialogCallback(undefined);
      setAuthDialogDefaultValues(undefined);
      setCustomTitle(undefined);
      setSkipSegmentation(false);
    }
  }, [authDialogOpen]);

  function updateUser(data: Partial<User>) {
    if (user) {
      setUser({
        ...user,
        ...data,
      });
    }
  }

  return (
    <userContext.Provider
      value={{
        user,
        reloadUser,
        logout,
        loaded,
        updateUser,
        displayAuthDialog,
      }}
    >
      {children}

      {authDialogRequested ? (
        <div className="hidden">
          <RequestedAuthDialog
            customTitle={customTitle}
            intent={authDialogIntent}
            defaultValues={authDialogDefaultValues}
            skipSegmentation={skipSegmentation}
            onOpenChange={(open) => {
              setAuthDialogOpen(open);
              if (!open) setAuthDialogRequested(false);
            }}
            onFinish={authDialogCallback}
          />
        </div>
      ) : null}
    </userContext.Provider>
  );
}
